import React, { useState, useRef, useEffect } from "react";
import { Heart, Swords, RotateCcw, ChevronLeft, FlaskConical, Microscope, CheckCircle, XCircle } from "lucide-react";
import { applyBoost, GAME_BOOST } from "../lib/tamagotchiUtils";
import { getTierEnemies } from "../data/battleData";
import { rollCardDrop, RARITY } from "../data/medicineCardData";
import { checkAchievements, TIER_STYLE } from "../data/achievementData";
import { diagnostics } from "../data/diagnosticData";

// ─── helpers ─────────────────────────────────────────────────────────────────

const HP_BAR_COLOR = (pct) => {
  if (pct > 0.5) return "bg-green-500";
  if (pct > 0.25) return "bg-yellow-400";
  return "bg-red-500";
};

const TREATMENT_STYLE = "border-blue-300 bg-blue-50 hover:bg-blue-100 text-blue-900";

const TREATMENT_RESULT_STYLE = {
  correct:  "bg-green-100 border-green-400 text-green-800",
  partial:  "bg-yellow-100 border-yellow-400 text-yellow-800",
  wrong:    "bg-red-50 border-red-300 text-red-700",
  harmful:  "bg-red-200 border-red-600 text-red-900",
  resisted: "bg-blue-100 border-blue-400 text-blue-900",
};

const TIER_META = {
  easy:   { label: "Easy",   border: "border-green-400",  text: "text-green-700",  bg: "bg-green-50"  },
  medium: { label: "Medium", border: "border-yellow-400", text: "text-yellow-700", bg: "bg-yellow-50" },
  hard:   { label: "Hard",   border: "border-red-400",    text: "text-red-700",   bg: "bg-red-50"    },
};

// ─── sub-components ──────────────────────────────────────────────────────────

const HpBar = ({ current, max }) => {
  const pct = Math.max(0, current / max);
  return (
    <div className="w-full">
      <div className="flex justify-between text-xs font-semibold mb-1">
        <span>{current}/{max}</span>
      </div>
      <div className="w-full bg-gray-200 rounded-full h-4 overflow-hidden border border-gray-300">
        <div className={`h-full rounded-full transition-all duration-500 ${HP_BAR_COLOR(pct)}`} style={{ width: `${pct * 100}%` }} />
      </div>
    </div>
  );
};

// ─── main component ───────────────────────────────────────────────────────────

const MedRPG = ({ onBack, userData, saveUserData, initialEnemy = null, onComplete = null }) => {
  // ── Zone-mode initial state ───────────────────────────────────────────────
  const _iDiag = initialEnemy ? (diagnostics[initialEnemy.id] || null) : null;
  const _iHP   = initialEnemy ? initialEnemy.hp : 0;

  // navigation
  const [phase, setPhase] = useState( // menu|tier|workup|diagnosis|battle|victory|defeat
    initialEnemy ? (_iDiag ? "workup" : "battle") : "menu"
  );
  const [selectedTier, setSelectedTier] = useState(null);
  const [enemy, setEnemy] = useState(initialEnemy);

  // workup / diagnosis state
  const [diagData, setDiagData] = useState(_iDiag);
  const [orderedTests, setOrderedTests] = useState(new Set());
  const [shuffledDiagChoices, setShuffledDiagChoices] = useState(() =>
    _iDiag ? [..._iDiag.diagnosisChoices].sort(() => Math.random() - 0.5) : []
  );
  const [selectedDiagnosis, setSelectedDiagnosis] = useState(null);
  const [diagnosisResult, setDiagnosisResult] = useState(null); // null | "correct" | "wrong"
  const [diagnosisBonus, setDiagnosisBonus] = useState(false);

  // battle state
  const battleRef = useRef({ enemyHP: _iHP, playerHP: 100 });
  const [enemyHP, setEnemyHP]       = useState(_iHP);
  const [playerHP, setPlayerHP]     = useState(100);
  const [isAnimating, setIsAnimating]         = useState(false);
  const [battleLog, setBattleLog]             = useState(() =>
    initialEnemy && !_iDiag
      ? [`⚔️ ${initialEnemy.name} is attacking your patient!`, `🩺 Choose your treatments wisely, Doctor!`]
      : []
  );
  const [lastResult, setLastResult]           = useState(null);
  const [usedTreatments, setUsedTreatments]   = useState(new Set());
  const [shuffledTreatments, setShuffledTreatments] = useState(() =>
    initialEnemy && !_iDiag
      ? [...initialEnemy.treatments].sort(() => Math.random() - 0.5)
      : []
  );
  const [shake, setShake]                     = useState({ enemy: false, player: false });
  const [enemyFlash, setEnemyFlash]           = useState(false);
  const [resistanceShield, setResistanceShield] = useState(false);
  const [coinsEarned, setCoinsEarned]         = useState(0);
  const [xpEarned, setXpEarned]               = useState(0);
  const [droppedCard, setDroppedCard]         = useState(null);
  const [newAchievements, setNewAchievements] = useState([]);
  const logEndRef = useRef(null);

  useEffect(() => { logEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [battleLog]);

  const addLog = (msg) => setBattleLog((prev) => [...prev, msg]);

  // ── pick a random enemy from a tier ──────────────────────────────────────
  const pickRandomEnemy = (tier) => {
    const pool = getTierEnemies(tier);
    const pick = pool[Math.floor(Math.random() * pool.length)];
    setSelectedTier(tier);
    selectEnemy(pick);
  };

  // ── pick an enemy → go to workup (or straight to battle if no diag data) ──
  const selectEnemy = (e) => {
    const dd = diagnostics[e.id] || null;
    setEnemy(e);
    setDiagData(dd);
    setOrderedTests(new Set());
    setSelectedDiagnosis(null);
    setDiagnosisResult(null);
    setDiagnosisBonus(false);
    if (dd) {
      setShuffledDiagChoices([...dd.diagnosisChoices].sort(() => Math.random() - 0.5));
      setPhase("workup");
    } else {
      enterBattle(e, false);
    }
  };

  // ── enter battle phase (called after workup or skip) ─────────────────────
  const enterBattle = (e, wasCorrect) => {
    const src = e || enemy;
    const startHP = wasCorrect ? Math.round(src.hp * 0.8) : src.hp;
    battleRef.current = { enemyHP: startHP, playerHP: 100 };
    setEnemyHP(startHP);
    setPlayerHP(100);
    setBattleLog([
      `⚔️ An unknown pathogen is attacking your patient!`,
      wasCorrect
        ? `🎯 Early diagnosis gave you an advantage — enemy starts at 80% HP!`
        : `🩺 Choose your treatments wisely, Doctor!`,
    ]);
    setLastResult(null);
    setUsedTreatments(new Set());
    setShuffledTreatments([...src.treatments].sort(() => Math.random() - 0.5));
    setIsAnimating(false);
    setPhase("battle");
  };

  // ── order a diagnostic test ───────────────────────────────────────────────
  const orderTest = (testId) => {
    setOrderedTests((prev) => new Set([...prev, testId]));
  };

  // ── submit diagnosis guess ────────────────────────────────────────────────
  const submitDiagnosis = () => {
    if (!selectedDiagnosis) return;
    const correct = selectedDiagnosis === diagData.correctDiagnosis;
    setDiagnosisResult(correct ? "correct" : "wrong");
    if (correct) setDiagnosisBonus(true);
  };

  // ── treatment logic ───────────────────────────────────────────────────────
  const applyTreatment = (treatment) => {
    if (isAnimating || usedTreatments.has(treatment.id)) return;
    setIsAnimating(true);
    setUsedTreatments((prev) => new Set([...prev, treatment.id]));

    // ── Resistance check ────────────────────────────────────────────────────
    const resistedClasses = enemy.resistantTo || [];
    if (treatment.antibioticClass && resistedClasses.includes(treatment.antibioticClass)) {
      const resistMsg = enemy.resistanceMessage || `${enemy.nickname} is resistant to this antibiotic class!`;
      addLog(`🛡️ ${treatment.name} — RESISTED! ${resistMsg}`);
      setLastResult({ ...treatment, type: "resisted", explanation: resistMsg });
      setResistanceShield(true);
      setTimeout(() => setResistanceShield(false), 700);
      // Enemy still gets to counter-attack
      setTimeout(() => {
        const attack = enemy.attacks[Math.floor(Math.random() * enemy.attacks.length)];
        const newPHP = Math.max(0, battleRef.current.playerHP - attack.damage);
        battleRef.current.playerHP = newPHP;
        setPlayerHP(newPHP);
        setShake((s) => ({ ...s, player: true }));
        setTimeout(() => setShake((s) => ({ ...s, player: false })), 500);
        addLog(`🦠 The pathogen ${attack.message} (−${attack.damage} HP)`);
        if (newPHP <= 0) setTimeout(() => setPhase("defeat"), 900);
        else setIsAnimating(false);
      }, 1300);
      return;
    }

    const curEnemyHP  = battleRef.current.enemyHP;
    const curPlayerHP = battleRef.current.playerHP;
    const isHarmful   = treatment.type === "harmful";
    const isWrong     = treatment.type === "wrong";
    const dmgToEnemy  = isHarmful ? 0 : treatment.damage;
    const dmgToPlayer = isHarmful ? Math.abs(treatment.damage) : isWrong ? 20 : 0;

    const newEnemyHP = Math.max(0, curEnemyHP - dmgToEnemy);
    battleRef.current.enemyHP = newEnemyHP;
    setEnemyHP(newEnemyHP);

    if (dmgToEnemy > 0) {
      setEnemyFlash(true);
      setShake((s) => ({ ...s, enemy: true }));
      setTimeout(() => { setEnemyFlash(false); setShake((s) => ({ ...s, enemy: false })); }, 500);
    }
    if (dmgToPlayer > 0) {
      const newPHP = Math.max(0, curPlayerHP - dmgToPlayer);
      battleRef.current.playerHP = newPHP;
      setPlayerHP(newPHP);
      setShake((s) => ({ ...s, player: true }));
      setTimeout(() => setShake((s) => ({ ...s, player: false })), 500);
    }

    const logMsg =
      treatment.type === "correct" ? `💚 ${treatment.name} — Super effective! (−${dmgToEnemy} HP)` :
      treatment.type === "partial" ? `💛 ${treatment.name} — Partially effective (−${dmgToEnemy} HP)` :
      treatment.type === "harmful" ? `💥 ${treatment.name} — BACKFIRED! (−${dmgToPlayer} patient HP!)` :
      `❌ ${treatment.name} — Wrong treatment! Patient worsens (−20 HP)`;
    addLog(logMsg);
    setLastResult(treatment);

    if (newEnemyHP <= 0) { setTimeout(handleVictory, 900); return; }
    if (dmgToPlayer > 0 && battleRef.current.playerHP <= 0) { setTimeout(() => setPhase("defeat"), 900); return; }

    setTimeout(() => {
      const attack = enemy.attacks[Math.floor(Math.random() * enemy.attacks.length)];
      const newPHP = Math.max(0, battleRef.current.playerHP - attack.damage);
      battleRef.current.playerHP = newPHP;
      setPlayerHP(newPHP);
      setShake((s) => ({ ...s, player: true }));
      setTimeout(() => setShake((s) => ({ ...s, player: false })), 500);
      addLog(`🦠 The pathogen ${attack.message} (−${attack.damage} HP)`);
      if (newPHP <= 0) setTimeout(() => setPhase("defeat"), 900);
      else setIsAnimating(false);
    }, 1300);
  };

  // ── victory ───────────────────────────────────────────────────────────────
  const handleVictory = async () => {
    const coins = enemy.reward.coins + (diagnosisBonus ? 15 : 0);
    const xp    = enemy.reward.xp;
    setCoinsEarned(coins);
    setXpEarned(xp);
    const card = rollCardDrop(userData.medicineCards || []);
    setDroppedCard(card);
    const afterBattle = {
      ...userData,
      coins: userData.coins + coins,
      xp: userData.xp + xp,
      avatarStats: applyBoost(userData.avatarStats, GAME_BOOST),
      defeatedEnemies: [...new Set([...(userData.defeatedEnemies || []), enemy.id])],
      medicineCards: card
        ? [...(userData.medicineCards || []), card.id]
        : (userData.medicineCards || []),
    };
    const unlocked = checkAchievements(afterBattle);
    setNewAchievements(unlocked);
    const newUserData = {
      ...afterBattle,
      achievements: [...(afterBattle.achievements || []), ...unlocked.map(a => a.id)],
    };
    await saveUserData(newUserData);
    setPhase("victory");
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // RENDERS
  // ═══════════════════════════════════════════════════════════════════════════

  // ── MENU ──────────────────────────────────────────────────────────────────
  if (phase === "menu") return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-purple-900 to-slate-900">
      <div className="max-w-2xl mx-auto px-4 py-8">
        <button onClick={onBack} className="mb-6 text-white/70 hover:text-white flex items-center gap-2 text-sm">
          <ChevronLeft className="w-4 h-4" /> Back to Games
        </button>
        <div className="text-center mb-10">
          <div className="text-7xl mb-4">🩺</div>
          <h1 className="text-4xl font-extrabold text-white mb-2">MedQuest RPG</h1>
          <p className="text-purple-200 text-lg">Diagnose. Treat. Save your patient.</p>
        </div>
        <div className="bg-white/10 backdrop-blur rounded-2xl p-6 mb-6 border border-white/20">
          <h2 className="text-white font-bold text-lg mb-3">How to Play</h2>
          <ul className="text-purple-100 text-sm space-y-2">
            <li>🔬 <strong>Workup first</strong> — order lab tests, imaging, and cultures to uncover the pathogen.</li>
            <li>🧠 <strong>Make your diagnosis</strong> — identify the enemy before you fight. Correct diagnosis = 80% starting HP bonus!</li>
            <li>💊 <strong>Choose treatments</strong> — right drugs deal big damage, wrong ones do nothing (or backfire!).</li>
            <li>☠️ <strong>Watch out</strong> — some wrong treatments actively harm your patient.</li>
          </ul>
        </div>
        <button onClick={() => setPhase("tier")} className="w-full py-4 rounded-2xl bg-gradient-to-r from-purple-500 to-indigo-500 hover:from-purple-400 hover:to-indigo-400 text-white font-bold text-xl shadow-lg transition-all transform hover:scale-105">
          Start Adventure ⚔️
        </button>
      </div>
    </div>
  );

  // ── TIER SELECT ───────────────────────────────────────────────────────────
  if (phase === "tier") return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-purple-900 to-slate-900">
      <div className="max-w-2xl mx-auto px-4 py-8">
        <button onClick={() => setPhase("menu")} className="mb-6 text-white/70 hover:text-white flex items-center gap-2 text-sm">
          <ChevronLeft className="w-4 h-4" /> Back
        </button>
        <h2 className="text-3xl font-extrabold text-white text-center mb-8">Choose Difficulty</h2>
        <div className="space-y-4">
          {["easy", "medium", "hard"].map((tier) => {
            const m = TIER_META[tier];
            const te = getTierEnemies(tier);
            return (
              <button key={tier} onClick={() => pickRandomEnemy(tier)}
                className={`w-full rounded-2xl p-6 border-2 ${m.border} ${m.bg} text-left transition-all hover:scale-[1.02] hover:shadow-lg`}>
                <div className="flex items-center justify-between">
                  <div>
                    <div className={`font-extrabold text-xl ${m.text}`}>{m.label}</div>
                    <div className="text-gray-600 text-sm mt-1">{te.length} possible pathogens · identity unknown</div>
                  </div>
                  <div className="text-3xl">{tier === "easy" ? "🌱" : tier === "medium" ? "⚡" : "💀"}</div>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );

  // ── ENEMY SELECT ──────────────────────────────────────────────────────────
  if (phase === "select") {
    const tierEnemies = getTierEnemies(selectedTier);
    const m = TIER_META[selectedTier];
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-purple-900 to-slate-900">
        <div className="max-w-2xl mx-auto px-4 py-8">
          <button onClick={() => setPhase("tier")} className="mb-6 text-white/70 hover:text-white flex items-center gap-2 text-sm">
            <ChevronLeft className="w-4 h-4" /> Back
          </button>
          <h2 className="text-3xl font-extrabold text-white text-center mb-2">Choose Your Battle</h2>
          <p className="text-purple-200 text-center text-sm mb-8">Each pathogen tests different clinical knowledge</p>
          <div className="space-y-4">
            {tierEnemies.map((e) => (
              <button key={e.id} onClick={() => selectEnemy(e)}
                className={`w-full rounded-2xl p-5 border-2 ${e.borderColor} bg-white text-left transition-all hover:scale-[1.02] hover:shadow-xl`}>
                <div className="flex items-center gap-4">
                  <div className={`text-4xl w-14 h-14 rounded-xl bg-gradient-to-br ${e.color} flex items-center justify-center shadow-md`}>
                    {e.emoji}
                  </div>
                  <div className="flex-1">
                    <div className="font-extrabold text-gray-900">{e.name}</div>
                    <div className="text-xs text-gray-500 mb-1">{e.type}</div>
                    <div className={`text-sm font-semibold ${m.text}`}>{e.disease}</div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-yellow-500 font-bold text-sm">+{e.reward.coins} 🪙</div>
                    <div className="text-purple-500 font-bold text-sm">+{e.reward.xp} XP</div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // ── WORKUP ────────────────────────────────────────────────────────────────
  if (phase === "workup" && diagData && enemy) {
    const orderedList = diagData.tests.filter((t) => orderedTests.has(t.id));
    const unordered   = diagData.tests.filter((t) => !orderedTests.has(t.id));
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-indigo-950 to-purple-950">
        <div className="max-w-xl mx-auto px-3 py-4">
          <button onClick={() => setPhase("tier")} className="mb-4 text-white/70 hover:text-white flex items-center gap-2 text-sm">
            <ChevronLeft className="w-4 h-4" /> Back
          </button>

          {/* Patient presentation */}
          <div className="bg-white rounded-2xl p-5 mb-4 shadow-xl border-2 border-indigo-200">
            <div className="flex items-center gap-2 mb-3">
              <span className="text-2xl">🏥</span>
              <span className="font-extrabold text-gray-900 text-base">Patient Presentation</span>
              <span className="ml-auto text-xs bg-indigo-100 text-indigo-700 font-bold px-2 py-1 rounded-full">Identity Unknown</span>
            </div>
            <p className="text-gray-800 text-sm mb-3 leading-relaxed">{diagData.presentation.intro}</p>
            <div className="space-y-1">
              {diagData.presentation.clues.map((c, i) => (
                <div key={i} className="flex items-start gap-2 text-sm text-gray-700">
                  <span className="text-indigo-400 mt-0.5">▸</span><span>{c}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Available tests */}
          {unordered.length > 0 && (
            <div className="mb-4">
              <div className="flex items-center gap-2 mb-2">
                <FlaskConical className="w-4 h-4 text-purple-300" />
                <span className="text-white text-sm font-bold">Order Diagnostic Tests</span>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {unordered.map((t) => (
                  <button key={t.id} onClick={() => orderTest(t.id)}
                    className="rounded-xl p-3 border-2 border-purple-300 bg-purple-50 hover:bg-purple-100 text-purple-900 text-left text-sm font-semibold transition-all hover:scale-[1.02]">
                    <span className="mr-1">{t.icon}</span>{t.name}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Ordered test results */}
          {orderedList.length > 0 && (
            <div className="mb-4">
              <div className="flex items-center gap-2 mb-2">
                <Microscope className="w-4 h-4 text-green-300" />
                <span className="text-white text-sm font-bold">Results</span>
              </div>
              <div className="space-y-2">
                {orderedList.map((t) => (
                  <div key={t.id} className="bg-white rounded-xl p-3 border-2 border-green-300">
                    <div className="font-bold text-gray-800 text-sm mb-1">{t.icon} {t.name}</div>
                    <div className="text-sm text-gray-700">{t.clue}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="space-y-2">
            <button onClick={() => setPhase("diagnosis")}
              disabled={orderedTests.size === 0}
              className="w-full py-3 rounded-xl font-bold text-white transition-all
                bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-400 hover:to-purple-400
                disabled:opacity-40 disabled:cursor-not-allowed">
              🧠 Make Diagnosis {orderedTests.size === 0 ? "(order at least 1 test)" : `(${orderedTests.size} test${orderedTests.size > 1 ? "s" : ""} ordered)`}
            </button>
            <button onClick={() => enterBattle(enemy, false)}
              className="w-full py-2 rounded-xl border-2 border-white/30 text-white/70 text-sm hover:border-white/60 hover:text-white transition-all">
              Skip workup — go straight to treatment
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── DIAGNOSIS ─────────────────────────────────────────────────────────────
  if (phase === "diagnosis" && diagData) {
    const orderedList = diagData.tests.filter((t) => orderedTests.has(t.id));
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-indigo-950 to-purple-950">
        <div className="max-w-xl mx-auto px-3 py-4">
          <button onClick={() => setPhase("workup")} className="mb-4 text-white/70 hover:text-white flex items-center gap-2 text-sm">
            <ChevronLeft className="w-4 h-4" /> Back to Workup
          </button>

          {/* Results summary */}
          <div className="bg-white rounded-2xl p-4 mb-4 shadow-xl border-2 border-indigo-200">
            <div className="font-extrabold text-gray-900 mb-3 flex items-center gap-2">
              <Microscope className="w-4 h-4 text-indigo-600" /> Your Results Summary
            </div>
            {orderedList.length === 0
              ? <p className="text-gray-500 text-sm italic">No tests ordered — making a clinical diagnosis!</p>
              : orderedList.map((t) => (
                <div key={t.id} className="mb-2 last:mb-0">
                  <span className="font-semibold text-sm text-gray-800">{t.icon} {t.name}: </span>
                  <span className="text-sm text-gray-700">{t.clue}</span>
                </div>
              ))
            }
          </div>

          {/* Diagnosis choices */}
          {!diagnosisResult && (
            <>
              <div className="text-white font-bold text-base mb-3">🩺 What is your diagnosis?</div>
              <div className="space-y-2 mb-4">
                {shuffledDiagChoices.map((choice) => (
                  <button key={choice.id} onClick={() => setSelectedDiagnosis(choice.id)}
                    className={`w-full rounded-xl p-4 border-2 text-left text-sm font-semibold transition-all
                      ${selectedDiagnosis === choice.id
                        ? "border-indigo-500 bg-indigo-100 text-indigo-900 scale-[1.02]"
                        : "border-gray-300 bg-white text-gray-800 hover:border-indigo-300 hover:bg-indigo-50"
                      }`}>
                    {choice.name}
                  </button>
                ))}
              </div>
              <button onClick={submitDiagnosis} disabled={!selectedDiagnosis}
                className="w-full py-3 rounded-xl bg-gradient-to-r from-indigo-500 to-purple-500 text-white font-bold disabled:opacity-40 disabled:cursor-not-allowed hover:from-indigo-400 hover:to-purple-400 transition-all">
                Confirm Diagnosis
              </button>
            </>
          )}

          {/* Result feedback */}
          {diagnosisResult === "correct" && (
            <div className="bg-green-50 border-2 border-green-400 rounded-2xl p-5 text-center">
              <CheckCircle className="w-10 h-10 text-green-500 mx-auto mb-2" />
              <div className="font-extrabold text-green-800 text-lg mb-1">Correct Diagnosis!</div>
              <div className="text-green-700 text-sm mb-4">Early identification means the pathogen is caught off-guard. Enemy starts at <strong>80% HP</strong> and you earn <strong>+15 bonus coins!</strong></div>
              <button onClick={() => enterBattle(enemy, true)}
                className="w-full py-3 rounded-xl bg-gradient-to-r from-green-500 to-emerald-500 text-white font-bold hover:from-green-400 hover:to-emerald-400 transition-all">
                ⚔️ Proceed to Treatment
              </button>
            </div>
          )}

          {diagnosisResult === "wrong" && (
            <div className="bg-red-50 border-2 border-red-400 rounded-2xl p-5 text-center">
              <XCircle className="w-10 h-10 text-red-500 mx-auto mb-2" />
              <div className="font-extrabold text-red-800 text-lg mb-1">Incorrect!</div>
              <div className="text-red-700 text-sm mb-4">Review your test results and try again — or proceed to treatment without the diagnosis bonus.</div>
              <div className="flex gap-2">
                <button onClick={() => { setDiagnosisResult(null); setSelectedDiagnosis(null); }}
                  className="flex-1 py-2 rounded-xl border-2 border-red-400 text-red-700 font-bold hover:bg-red-100 transition-all text-sm">
                  Try Again
                </button>
                <button onClick={() => enterBattle(enemy, false)}
                  className="flex-1 py-2 rounded-xl bg-gray-700 text-white font-bold hover:bg-gray-600 transition-all text-sm">
                  Skip to Battle
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ── BATTLE ────────────────────────────────────────────────────────────────
  if (phase === "battle" && enemy) {
    const allUsed = enemy.treatments.filter((t) => t.type !== "harmful").every((t) => usedTreatments.has(t.id));
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-indigo-950 to-purple-950">
        <div className="max-w-xl mx-auto px-3 py-4">

          {/* Patient HP */}
          <div className="bg-white/10 backdrop-blur rounded-xl p-3 mb-3 border border-white/20">
            <div className="flex items-center gap-2 mb-2">
              <Heart className="w-4 h-4 text-red-400" />
              <span className="text-white text-sm font-bold">Patient Health</span>
              {diagnosisBonus && <span className="ml-auto text-xs bg-green-500/30 text-green-300 font-bold px-2 py-0.5 rounded-full">Early Diagnosis Bonus Active</span>}
            </div>
            <div className={shake.player ? "animate-bounce" : ""}>
              <HpBar current={playerHP} max={100} />
            </div>
          </div>

          {/* Enemy */}
          <div className={`rounded-2xl p-5 mb-3 border-2 ${enemy.borderColor} bg-white/95 shadow-xl relative overflow-hidden`}>
            {resistanceShield && (
              <div className="absolute inset-0 flex items-center justify-center bg-blue-400/30 z-10 rounded-2xl animate-pulse">
                <span className="text-4xl">🛡️</span>
              </div>
            )}
            <div className="flex items-center gap-4 mb-3">
              <div className={`w-28 h-28 rounded-xl bg-gradient-to-br ${enemy.color} flex items-center justify-center shadow-md transition-all duration-150 overflow-hidden ${shake.enemy ? "scale-110" : ""} ${enemyFlash ? "brightness-150" : ""} ${resistanceShield ? "opacity-60" : ""}`}>
                {enemy.image
                  ? <img src={enemy.image} alt={enemy.name} className="w-full h-full object-cover" />
                  : <span className="text-5xl">{enemy.emoji}</span>
                }
              </div>
              <div className="flex-1">
                <div className="font-extrabold text-gray-900">{diagnosisBonus ? enemy.name : "??? Unknown Pathogen"}</div>
                <div className="text-xs text-gray-500 mb-2">{diagnosisBonus ? enemy.type : "Identity unconfirmed"}</div>
                <HpBar current={enemyHP} max={enemy.hp} />
              </div>
            </div>
            <p className="text-xs text-gray-600 italic">{enemy.description}</p>
          </div>

          {/* Battle log */}
          <div className="bg-black/40 backdrop-blur rounded-xl p-3 mb-3 border border-white/10 h-24 overflow-y-auto">
            {battleLog.map((msg, i) => <p key={i} className="text-xs text-gray-200 leading-relaxed">{msg}</p>)}
            <div ref={logEndRef} />
          </div>

          {/* Last result */}
          {lastResult && (
            <div className={`rounded-xl p-3 mb-3 border-2 text-sm ${TREATMENT_RESULT_STYLE[lastResult.type] || "bg-gray-50 border-gray-300 text-gray-700"}`}>
              <div className="font-bold mb-0.5">
                {lastResult.type === "resisted" ? "🛡️ RESISTED — " : ""}{lastResult.name}
              </div>
              <div>{lastResult.explanation}</div>
            </div>
          )}

          {/* Treatments */}
          <div className="mb-3">
            <div className="flex items-center gap-2 mb-2">
              <Swords className="w-4 h-4 text-purple-300" />
              <span className="text-white text-sm font-bold">Choose Treatment</span>
              {isAnimating && <span className="text-yellow-300 text-xs ml-auto animate-pulse">Processing...</span>}
            </div>
            <div className="grid grid-cols-2 gap-2">
              {shuffledTreatments.map((t) => {
                const used = usedTreatments.has(t.id);
                return (
                  <button key={t.id} onClick={() => applyTreatment(t)} disabled={isAnimating || used}
                    className={`rounded-xl p-3 border-2 text-left transition-all text-sm font-semibold
                      ${used ? "opacity-40 cursor-not-allowed bg-gray-100 border-gray-200 text-gray-400" : TREATMENT_STYLE}
                      ${!used && !isAnimating ? "hover:scale-[1.03] hover:shadow-md" : ""}`}>
                    {t.name}
                    {used && <span className="text-xs ml-1 font-normal">(used)</span>}
                  </button>
                );
              })}
            </div>
          </div>

          {allUsed && enemyHP > 0 && (
            <button onClick={() => setPhase("defeat")} className="w-full py-2 rounded-xl bg-red-700/80 text-white text-sm font-semibold border border-red-500">
              Patient deteriorated — give up
            </button>
          )}
        </div>
      </div>
    );
  }

  // ── VICTORY ───────────────────────────────────────────────────────────────
  if (phase === "victory") return (
    <div className="min-h-screen bg-gradient-to-br from-green-900 via-emerald-800 to-teal-900 flex items-center justify-center px-4">
      <div className="max-w-md w-full bg-white rounded-3xl p-8 shadow-2xl text-center">
        <div className="text-6xl mb-4">🏆</div>
        <h2 className="text-3xl font-extrabold text-gray-900 mb-1">Patient Saved!</h2>
        <p className="text-gray-500 mb-2">You defeated {enemy?.name}</p>
        {diagnosisBonus && <p className="text-green-600 font-semibold text-sm mb-4">+15 bonus coins for correct diagnosis! 🎯</p>}
        <div className="flex justify-center gap-8 mb-6">
          <div className="bg-yellow-50 rounded-2xl px-6 py-4 border-2 border-yellow-300">
            <div className="text-3xl font-extrabold text-yellow-600">+{coinsEarned}</div>
            <div className="text-sm text-yellow-700 font-semibold">Coins 🪙</div>
          </div>
          <div className="bg-purple-50 rounded-2xl px-6 py-4 border-2 border-purple-300">
            <div className="text-3xl font-extrabold text-purple-600">+{xpEarned}</div>
            <div className="text-sm text-purple-700 font-semibold">XP ⭐</div>
          </div>
        </div>
        {droppedCard && (() => {
          const r = RARITY[droppedCard.rarity];
          return (
            <div
              className="rounded-2xl p-4 mb-4 border-2 text-left"
              style={{ background: r.bg, borderColor: r.border, boxShadow: `0 0 16px ${r.glow}` }}
            >
              <div className="text-xs font-bold mb-2" style={{ color: r.color }}>
                ✨ Medicine Card Found!
              </div>
              <div className="flex gap-3 items-center">
                <div className="text-4xl">{droppedCard.emoji}</div>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-extrabold text-gray-900">{droppedCard.name}</span>
                    <span
                      className="text-xs px-2 py-0.5 rounded-full font-semibold"
                      style={{ background: r.glow, color: r.color }}
                    >{r.label}</span>
                  </div>
                  <div className="text-xs mt-0.5" style={{ color: r.color }}>{droppedCard.category}</div>
                  <p className="text-xs italic text-gray-500 mt-0.5">"{droppedCard.tagline}"</p>
                </div>
              </div>
            </div>
          );
        })()}
        {newAchievements.length > 0 && (
          <div className="mb-4 space-y-2">
            {newAchievements.map(ach => {
              const t = TIER_STYLE[ach.tier];
              return (
                <div
                  key={ach.id}
                  className="rounded-2xl p-3 border-2 flex items-center gap-3 text-left"
                  style={{ background: t.bg, borderColor: t.border, boxShadow: `0 0 12px ${t.glow}` }}
                >
                  <div className="text-3xl flex-shrink-0">{ach.emoji}</div>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-bold mb-0.5" style={{ color: t.color }}>
                      🏅 Achievement Unlocked!
                    </div>
                    <div className="font-extrabold text-gray-900 text-sm">{ach.name}</div>
                    <div className="text-xs text-gray-500">{ach.description}</div>
                  </div>
                  <span
                    className="text-xs px-2 py-0.5 rounded-full font-semibold flex-shrink-0"
                    style={{ background: t.glow, color: t.color }}
                  >{t.label}</span>
                </div>
              );
            })}
          </div>
        )}
        {enemy?.funFact && (
          <div className="bg-blue-50 rounded-xl p-4 mb-6 border border-blue-200 text-left">
            <div className="text-xs font-bold text-blue-600 mb-1">🧬 Fun Fact</div>
            <p className="text-sm text-blue-900">{enemy.funFact}</p>
          </div>
        )}
        <button onClick={() => setPhase("report")}
          className="w-full mb-3 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-500 text-white font-bold text-sm transition-all flex items-center justify-center gap-2">
          📋 Case Report
        </button>
        <div className="flex gap-3">
          <button onClick={() => selectEnemy(enemy)}
            className="flex-1 py-3 rounded-xl bg-gradient-to-r from-green-500 to-emerald-500 text-white font-bold hover:from-green-400 hover:to-emerald-400 transition-all flex items-center justify-center gap-2">
            <RotateCcw className="w-4 h-4" /> Rematch
          </button>
          {!onComplete && (
            <button onClick={() => pickRandomEnemy(selectedTier)}
              className="flex-1 py-3 rounded-xl bg-gradient-to-r from-purple-500 to-indigo-500 text-white font-bold hover:from-purple-400 hover:to-indigo-400 transition-all flex items-center justify-center gap-2">
              <Swords className="w-4 h-4" /> Next Battle
            </button>
          )}
        </div>
        <button onClick={onComplete ?? onBack} className="mt-3 w-full py-2 rounded-xl border border-gray-300 text-gray-600 text-sm hover:bg-gray-50 transition-all">
          {onComplete ? "🗺️ Return to Town" : "Back to Games"}
        </button>
      </div>
    </div>
  );

  // ── CASE REPORT ───────────────────────────────────────────────────────────
  if (phase === "report" && enemy) {
    const groups = [
      { type: "correct", label: "Effective Treatments", icon: "✅", style: "bg-green-50 border-green-300" },
      { type: "partial", label: "Partially Effective",  icon: "⚠️", style: "bg-yellow-50 border-yellow-300" },
      { type: "wrong",   label: "Ineffective",          icon: "❌", style: "bg-red-50 border-red-200" },
      { type: "harmful", label: "Harmful",              icon: "☠️", style: "bg-red-100 border-red-500" },
    ];
    const m = selectedTier ? TIER_META[selectedTier] : TIER_META.easy;
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-indigo-950 to-purple-950">
        <div className="max-w-xl mx-auto px-3 py-4 pb-8">
          <button onClick={() => setPhase("victory")} className="mb-4 text-white/70 hover:text-white flex items-center gap-2 text-sm">
            <ChevronLeft className="w-4 h-4" /> Back to Results
          </button>

          {/* Pathogen summary */}
          <div className={`bg-white rounded-2xl p-5 mb-4 shadow-xl border-2 ${enemy.borderColor}`}>
            <div className="flex items-center gap-4">
              <div className={`w-20 h-20 rounded-xl bg-gradient-to-br ${enemy.color} flex items-center justify-center shadow-md overflow-hidden shrink-0`}>
                {enemy.image
                  ? <img src={enemy.image} alt={enemy.name} className="w-full h-full object-cover" />
                  : <span className="text-4xl">{enemy.emoji}</span>
                }
              </div>
              <div>
                <div className="font-extrabold text-gray-900 text-lg">{enemy.name}</div>
                <div className="text-xs text-gray-500">{enemy.type}</div>
                <div className={`text-sm font-bold mt-0.5 ${m.text}`}>{enemy.disease}</div>
              </div>
            </div>
          </div>

          {/* Treatment breakdown */}
          <div className="text-white font-bold text-sm mb-3">📊 Treatment Analysis</div>
          <div className="space-y-3 mb-4">
            {groups.map(({ type, label, icon, style }) => {
              const items = enemy.treatments.filter(t => t.type === type);
              if (items.length === 0) return null;
              return (
                <div key={type} className={`rounded-2xl border-2 p-4 ${style} bg-white`}>
                  <div className="font-bold text-gray-800 text-sm mb-2">{icon} {label}</div>
                  <div className="space-y-2">
                    {items.map(t => (
                      <div key={t.id} className="border-l-4 pl-3" style={{ borderColor: type === "correct" ? "#22c55e" : type === "partial" ? "#eab308" : "#ef4444" }}>
                        <div className="font-semibold text-gray-900 text-sm">{t.name}</div>
                        <div className="text-xs text-gray-600 mt-0.5">{t.explanation}</div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Fun fact */}
          {enemy.funFact && (
            <div className="bg-blue-50 rounded-2xl p-4 border-2 border-blue-200 mb-4">
              <div className="text-xs font-bold text-blue-600 mb-1">🧬 Clinical Pearl</div>
              <p className="text-sm text-blue-900">{enemy.funFact}</p>
            </div>
          )}

          <button onClick={() => setPhase("victory")}
            className="w-full py-3 rounded-xl bg-gradient-to-r from-green-500 to-emerald-500 text-white font-bold hover:from-green-400 hover:to-emerald-400 transition-all">
            Back to Victory Screen
          </button>
        </div>
      </div>
    );
  }

  // ── DEFEAT ────────────────────────────────────────────────────────────────
  if (phase === "defeat") return (
    <div className="min-h-screen bg-gradient-to-br from-red-900 via-rose-900 to-gray-900 flex items-center justify-center px-4">
      <div className="max-w-md w-full bg-white rounded-3xl p-8 shadow-2xl text-center">
        <div className="text-6xl mb-4">💔</div>
        <h2 className="text-3xl font-extrabold text-gray-900 mb-1">Patient Lost</h2>
        <p className="text-gray-500 mb-6">{enemy?.name} was too powerful this time.</p>
        {enemy?.funFact && (
          <div className="bg-amber-50 rounded-xl p-4 mb-6 border border-amber-200 text-left">
            <div className="text-xs font-bold text-amber-600 mb-1">📖 Learn from it</div>
            <p className="text-sm text-amber-900">{enemy.funFact}</p>
          </div>
        )}
        <div className="flex gap-3">
          <button onClick={() => selectEnemy(enemy)}
            className="flex-1 py-3 rounded-xl bg-gradient-to-r from-red-500 to-rose-500 text-white font-bold hover:from-red-400 hover:to-rose-400 transition-all flex items-center justify-center gap-2">
            <RotateCcw className="w-4 h-4" /> Try Again
          </button>
          {!onComplete && (
            <button onClick={() => setPhase("tier")}
              className="flex-1 py-3 rounded-xl bg-gradient-to-r from-gray-600 to-slate-700 text-white font-bold transition-all flex items-center justify-center gap-2">
              <ChevronLeft className="w-4 h-4" /> Change Difficulty
            </button>
          )}
        </div>
        <button onClick={onComplete ?? onBack} className="mt-3 w-full py-2 rounded-xl border border-gray-300 text-gray-600 text-sm hover:bg-gray-50 transition-all">
          {onComplete ? "🗺️ Return to Town" : "Back to Games"}
        </button>
      </div>
    </div>
  );

  return null;
};

export default MedRPG;
